import { useState, useEffect, Fragment } from 'react';
import { IconClose } from '@yj-intl/riff-react';
import styled from 'styled-components';

import { radius, opacity, transition, fontSize } from '../../../../../common/css/setting/variable';

const StyledIconClose = styled(IconClose)`
  color: ${props => props.theme.news.palette.text_sub_lv1_common};
`;

/**
 * DISLIKEボタン(xボタン,非表示にするボタン&xボタン)のWrap
 * isSetting: DISLIKE完了画面表示のときtrueなのでtrueの場合は非表示にする
 *
 * @param root0
 * @param root0.$commentHeight
 * @param root0.$isSetting
 */
const DislikeWrap = styled.div<{ $commentHeight: number; $isSetting: boolean }>`
  position: absolute;
  right: 0;
  width: 100%;
  ${({ $commentHeight }) => `bottom: ${$commentHeight + 0}px;`}
  ${({ $isSetting }) => ($isSetting ? 'display: none;' : '')}
`;

/**
 * xボタン
 *
 * @param root0
 * @param root0.$isShowIcon
 */
const OpenIcon = styled.div<{ $isShowIcon: boolean }>`
  position: absolute;
  right: 0;
  bottom: 8px;
  margin-right: 14px;
  transition: ${transition.timeline_likeButton_common};
  ${({ $isShowIcon }) =>
    $isShowIcon
      ? `opacity: ${opacity.show_common}; visibility: visible;`
      : `opacity: ${opacity.hidden_common}; visibility: hidden;`}
`;
const OpenIconButton = styled.button`
  display: block;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`;

/**
 * 非表示に追加したあとの完了メッセージモジュール
 *
 * @param root0
 * @param root0.$isSetting
 */
const CompletionSetting = styled.div<{ $isSetting: boolean }>`
  justify-content: space-between;
  padding: 12px;
  ${({ $isSetting }) => ($isSetting ? 'display: flex;' : 'display: none;')}
  font-size: ${fontSize.px14};
`;

const CompletionSettingButton = styled.button`
  color: ${props => props.theme.news.palette.link_pc};
  &:hover {
    color: ${props => props.theme.news.palette.link_hover_pc};
    text-decoration: underline;
  }
`;

/**
 * DISLIKEの操作モジュール(非表示にするリンクのバルーン)
 *
 * @param props
 * @param props.$commentHeight
 * @param props.className
 * @param props.$isOpen
 */
const ModuleBalloon = styled.div<{ $commentHeight: number; $isOpen: boolean }>`
  position: absolute;
  right: 8px;
  ${({ $commentHeight }) => `bottom: ${$commentHeight + 40}px;`}
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  /* 以下,Balloonの三角用 */
  &::before,
  &::after {
    position: absolute;
    right: 5px;
    bottom: -5px;
    width: 15px;
    height: 15px;
    content: ' ';
    transform: rotate(50deg) skew(10deg);
  }
  &::before {
    background-color: ${props => props.theme.news.palette.bg_lv1_common};
    border: 1px solid #e6e6e6;
    box-shadow: 0 2px 2px 1px rgb(0 0 0 / 10%);
  }
  &::after {
    background-color: ${props => props.theme.news.palette.bg_lv1_common};
  }
`;
const ModuleItem = styled.div`
  position: relative;
  font-size: ${fontSize.px16};
  background-color: ${props => props.theme.news.palette.bg_lv1_common};
  border: 1px solid #e6e6e6;
  border-radius: ${radius.middle_common};
  box-shadow: 0 1px 2px 2px rgb(0 0 0 / 10%);
`;
const ModuleButton = styled.button`
  padding: 8px 12px !important;
  line-height: 1.5;
  color: ${props => props.theme.news.palette.link_pc};
`;

interface IProps {
  contentId: string;
  isSetting: boolean;
  addDislikeForGrandchild: () => void | null;
  removeDislikeForGrandchild: () => void | null;
  articlePos: number; // DISLIKE完了画面の罫線表示の調整で利用
  commentPos: number | null; // 記事にコメント棚出しがある場合のボタン高さ調整で利用
  dislikePos: number | null; // DISLIKE用のULTのposで利用
  setIsOpenDislikePopUp: (contentId: string, isOpenDislikePopUp: boolean) => void;
  isOpenDislikePopUp: boolean;
}

const FeedArticleDislike: React.FC<IProps> = props => {
  const [isShowIcon, setShowIcon] = useState<boolean>(true);
  const [commentHeight, setCommentHeight] = useState<number>(0);

  /**
   * xボタンをクリックした場合
   */
  const hanldeClickDislikeToggle = () => props.setIsOpenDislikePopUp(props.contentId, !props.isOpenDislikePopUp);

  /**
   * DISLIKE追加ボタンを押した場合
   */
  const addDislike = () => {
    // EventLogを落とす
    if (window.customLogger) {
      window.customLogger.logEvent('hd-lst', { shcid: props.contentId });
    }
    // 画面が元に戻ったときにあわせて初期表示(xボタン表示,リンクモジュール非表示)に戻しておく
    props.setIsOpenDislikePopUp(props.contentId, false);
    // ボタン系はひとまずすべて非表示にしておく
    setShowIcon(false);
    // DISLIKEの情報を登録する
    props.addDislikeForGrandchild();
  };
  /**
   * DISLIKEを削除(元に戻す)リンクを押した場合
   */
  const removeDislike = () => {
    // EventLogを落とす
    if (window.customLogger) {
      window.customLogger.logEvent('unhd-lst', { shcid: props.contentId });
    }
    // 初期表示に戻す
    setShowIcon(true);
    // DISLIKEの情報を登録する
    props.removeDislikeForGrandchild();
  };

  // componentDidMountと同等の動き
  useEffect(() => {
    // コメント棚出しがある場合そのモジュールの高さを取得してDISLIKE用のモジュールの高さ調整に利用する
    if (props.commentPos !== null) {
      const commentHeightResponse = getCommentModuleHeight();
      if (commentHeightResponse !== null) {
        setCommentHeight(commentHeightResponse);
      } else {
        // 取得ができていない場合表示崩れを防ぐためにすべてトルツメにする
        setShowIcon(false);
      }
    }
  }, []);

  // コメント棚出しモジュールの高さを取得
  const getCommentModuleHeight = (): number | null => {
    if (props.commentPos !== null) {
      const commentElements = Array.prototype.slice.call(document.getElementsByClassName('newsFeed_comment'));
      if (commentElements.length >= props.commentPos) {
        const height = commentElements[props.commentPos - 1].offsetHeight;
        // バルーンの矢印部分の高さを足して返却
        return height && height > 0 ? height + 8 : null;
      }
      return null;
    }
    return null;
  };

  return (
    <Fragment>
      <DislikeWrap $commentHeight={commentHeight} $isSetting={props.isSetting}>
        {/* xボタン */}
        <OpenIcon $isShowIcon={isShowIcon}>
          <OpenIconButton
            data-cl-params={`_cl_vmodule:st_maj;_cl_link:feedback;_cl_position:${props.dislikePos};`}
            onClick={hanldeClickDislikeToggle}
            aria-label="記事の非表示設定を開く"
          >
            <StyledIconClose size={14} />
          </OpenIconButton>
        </OpenIcon>
        {/* 非表示にするリンクのバルーン */}
        <ModuleBalloon
          id={`newsFeed_item_dislike_balloon_id-${props.articlePos}`}
          $commentHeight={commentHeight}
          $isOpen={props.isOpenDislikePopUp}
        >
          <ModuleItem>
            <ModuleButton
              data-cl-params={`_cl_vmodule:st_maj;_cl_link:hidelist;_cl_position:${props.dislikePos};`}
              onClick={addDislike}
            >
              類似する記事の表示を減らす
            </ModuleButton>
          </ModuleItem>
        </ModuleBalloon>
      </DislikeWrap>
      {/* 非表示処理完了表示 */}
      <CompletionSetting $isSetting={props.isSetting}>
        <div>類似する記事の表示が減ります</div>
        <div>
          <CompletionSettingButton
            data-cl-params={`_cl_vmodule:st_maj;_cl_link:undo;_cl_position:${props.dislikePos};`}
            onClick={removeDislike}
          >
            元に戻す
          </CompletionSettingButton>
        </div>
      </CompletionSetting>
    </Fragment>
  );
};

export default FeedArticleDislike;
